import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Button, Modal, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useAppDispatch, useAppSelector } from 'hooks';
import { dismissIntegrityBlock } from 'reducers/colluderRestrictions';
import { selectIntegrityBlock } from 'reducers/colluderRestrictions.selectors';
import {
  MODAL_BODY_TABLE,
  MODAL_BODY_TOURNAMENT,
  MODAL_HEADER_LABEL,
  MODAL_TITLE_TABLE,
  MODAL_TITLE_TOURNAMENT
} from 'types/colluder';

// EN-10064 — colluder hard-block modal.
//
// Opened either by App.tsx (engine `gotJoinGame` INTEGRITY_RESTRICTION fallback)
// or by the lobby tap-block guard. Single dismiss action — no waitlist CTA: a
// restricted player is held in the waitlist server-side (BR-8), so we never
// invite them to join a queue they can't be served from while the opponent sits.
const IntegrityBlockModal = () => {
  const dispatch = useAppDispatch();
  const block = useAppSelector(selectIntegrityBlock);
  const dismiss = () => dispatch(dismissIntegrityBlock());

  const isTournament = block.scope === 'tournament';
  const title = isTournament ? MODAL_TITLE_TOURNAMENT : MODAL_TITLE_TABLE;
  const body = isTournament ? MODAL_BODY_TOURNAMENT : MODAL_BODY_TABLE;

  return (
    <Modal open={block.open} onClose={dismiss}>
      <Box sx={modalStyle}>
        <Box sx={headerStyle}>
          <LockOutlinedIcon sx={{ fontSize: 16 }} aria-hidden />
          <Box component='span' sx={headerLabelStyle}>
            {MODAL_HEADER_LABEL}
          </Box>
        </Box>

        <Typography component='h2' sx={titleStyle}>
          {title}
        </Typography>

        <Box sx={bodyStyle}>{body}</Box>

        <Button fullWidth disableElevation sx={closeButtonStyle} onClick={dismiss}>
          Close
        </Button>
      </Box>
    </Modal>
  );
};

export default IntegrityBlockModal;

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: '#1b1a18',
  color: '#fff',
  padding: '1.5rem',
  width: 'min(94vw, 420px)',
  borderRadius: '14px',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)'
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  color: '#e8a33d',
  marginBottom: '0.85rem'
};

const headerLabelStyle = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase'
};

const titleStyle = {
  // Both the table and (longer) tournament titles must stay one line — scale the
  // font with viewport and never wrap.
  fontSize: 'clamp(0.78rem, 3.6vw, 1rem)',
  fontWeight: 700,
  lineHeight: 1.25,
  whiteSpace: 'nowrap',
  marginBottom: '0.6rem'
};

const bodyStyle = {
  fontSize: '0.95rem',
  lineHeight: 1.45,
  color: 'rgba(255, 255, 255, 0.6)',
  marginBottom: '1.6rem'
};

const closeButtonStyle = {
  backgroundColor: '#ef8a1e',
  color: '#1b1a18',
  fontWeight: 700,
  fontSize: '1rem',
  textTransform: 'none',
  borderRadius: '10px',
  padding: '0.7rem',
  '&:hover': { backgroundColor: '#f0972f' }
};
